import { useMemo } from 'react'
import { Autocomplete, Chip, TextField } from '@mui/material'
import type { FC } from 'react'

import type { FilterOption } from '@/utilities/types/shared-filter'

interface SelectFilterProps {
  field: string
  label: string
  value: string[]
  options?: FilterOption[] | Record<string, FilterOption[]>
  onChange: (field: string, value: string[]) => void
  isMulti?: boolean
  tab?: string
}

export const UserSelectFilter: FC<SelectFilterProps> = ({
  field,
  label,
  value = [],
  options = [],
  onChange,
  isMulti = true,
  tab,
}) => {
  const processedOptions: FilterOption[] = useMemo(() => {
    if (Array.isArray(options)) {
      return options
    } else if (tab && options && options[tab]) {
      return options[tab]
    }
    return []
  }, [options, tab])

  const selectedOptions = useMemo(() => {
    return processedOptions.filter((option) => {
      const optionValue =
        typeof option.value === 'string' ? option.value : String(option.value)
      return value.includes(optionValue)
    })
  }, [processedOptions, value])

  const handleChange = (
    _: any,
    newValue: FilterOption | FilterOption[] | null,
  ) => {
    if (Array.isArray(newValue)) {
      onChange(
        field,
        newValue.map((option) => option.value),
      )
    } else if (newValue) {
      onChange(field, [newValue.value])
    } else {
      onChange(field, [])
    }
  }

  return (
    <Autocomplete
      multiple={isMulti}
      options={processedOptions}
      value={isMulti ? selectedOptions : selectedOptions[0] || null}
      onChange={handleChange}
      getOptionLabel={(option) => {
        const label = option.label
        return typeof label === 'string' ? label : String(label)
      }}
      isOptionEqualToValue={(option, value) => {
        const optionValue =
          typeof option.value === 'string' ? option.value : String(option.value)
        const compareValue =
          typeof value.value === 'string' ? value.value : String(value.value)
        return optionValue === compareValue
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          size="small"
          placeholder={`Select ${label}`}
        />
      )}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option.value}
            label={option.label}
            size="small"
          />
        ))
      }
      disableCloseOnSelect={isMulti}
      sx={{
        '& .MuiOutlinedInput-root': {
          padding: '3px 8px',
        },
      }}
    />
  )
}
